import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  getDisplayName,
  formatTooltipValue,
  ECHARTS_OPTS,
} from './chartConfig';

export const ParallelChart: React.FC<ChartComponentProps> = ({ chart, height = 360, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const keys = Object.keys(data[0] || {}).filter(k => k !== 'name');
    if (keys.length === 0) return {};

    const parallelAxis: any[] = keys.map((k, i) => {
      const isNumeric = data.every(d => typeof d[k] === 'number' || !isNaN(Number(d[k])));
      if (isNumeric) {
        const vals = data.map(d => Number(d[k]) || 0);
        return {
          dim: i,
          name: getDisplayName(chart, k),
          type: 'value',
          min: Math.min(...vals) * 0.9,
          max: Math.max(...vals) * 1.1,
          nameTextStyle: { fontSize: 11, color: '#7B6B72' },
        };
      }
      const categories = [...new Set(data.map(d => String(d[k] ?? '')))];
      return {
        dim: i,
        name: getDisplayName(chart, k),
        type: 'category',
        data: categories,
        nameTextStyle: { fontSize: 11 },
      };
    });

    const seriesData = data.map(d =>
      keys.map(k => {
        const axis = parallelAxis[keys.indexOf(k)];
        if (axis.type === 'category') {
          return axis.data.indexOf(String(d[k] ?? ''));
        }
        return Number(d[k]) || 0;
      })
    );

    return {
      ...base,
      parallelAxis,
      parallel: {
        left: 60,
        right: 60,
        top: 40,
        bottom: 30,
      },
      tooltip: { show: false },
      series: [
        {
          type: 'parallel',
          lineStyle: { width: 1.5, opacity: 0.5 },
          emphasis: { lineStyle: { width: 3, opacity: 1 } },
          data: seriesData,
          smooth: true,
        },
      ],
      legend: { show: false },
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
